import type {
	BillingProviderCreateWebhookInput,
	BillingProviderUpdateWebhookInput,
} from "../../types.ts";

/**
 * mollie-api-typescript `webhooks.create` outbound `eventTypes` is
 * `string[] | WebhookEventTypes` (one enum value). Mollie `POST /v2/webhooks`
 * treats a JSON array as unset and returns 422 "No event types were set".
 * The wire value is a comma-separated string (same as Mollie's curl sample).
 * Do not send that string through Speakeasy `webhooks.create` — Zod drops it.
 */
export function encodeMollieWebhookEventTypes(
	eventTypes: readonly string[],
): string {
	const encoded = eventTypes
		.map((entry) => entry.trim())
		.filter((entry) => entry.length > 0)
		.join(",");
	if (encoded.length === 0) {
		throw new Error("Mollie webhook create requires at least one event type.");
	}
	return encoded;
}

export function decodeMollieWebhookEventTypes(value: unknown): string[] {
	if (typeof value === "string") {
		return value
			.split(",")
			.map((entry) => entry.trim())
			.filter((entry) => entry.length > 0);
	}
	if (Array.isArray(value)) {
		return value.filter((entry): entry is string => typeof entry === "string");
	}
	return [];
}

export function mapCreateWebhookToMollieSdk(input: {
	input: BillingProviderCreateWebhookInput;
	testMode: boolean;
}): Record<string, unknown> {
	return {
		idempotencyKey: input.input.idempotencyKey,
		requestBody: {
			eventTypes: encodeMollieWebhookEventTypes(input.input.eventTypes),
			name: input.input.name,
			testmode: input.testMode,
			url: input.input.url,
		},
	};
}

export function mapUpdateWebhookToMollieSdk(input: {
	input: BillingProviderUpdateWebhookInput;
	testMode: boolean;
}): Record<string, unknown> {
	const requestBody: Record<string, unknown> = {
		testmode: input.testMode,
	};
	if (input.input.eventTypes) {
		requestBody.eventTypes = encodeMollieWebhookEventTypes(
			input.input.eventTypes,
		);
	}
	if (input.input.name) {
		requestBody.name = input.input.name;
	}
	if (input.input.url) {
		requestBody.url = input.input.url;
	}
	return {
		requestBody,
		webhookId: input.input.id,
	};
}
